import { CommonModule } from '@angular/common';
import {
	ChangeDetectionStrategy,
	Component,
	HostListener,
	OnDestroy,
	OnInit
} from '@angular/core';
import {
	AbstractControl,
	FormBuilder,
	FormGroup,
	FormsModule,
	ReactiveFormsModule,
	ValidationErrors,
	Validators
} from '@angular/forms';
import { MatDialogRef } from '@angular/material/dialog';
import { MaterialModule } from '@app/material.module';
import { AuthService } from '@core/services/auth.service';
import { ReviewEntriesService } from '@pages/review-entries/review-entries.service';
import { SharedService } from '@shared/shared.service';
import { ToastrService } from 'ngx-toastr';
import { Subscription } from 'rxjs';
import { SelectDropdownComponent } from '../select-dropdown/select-dropdown.component';
import { SharedModule } from '../shared.module';

@Component({
	selector: 'app-it-password-reset',
	templateUrl: './it-password-reset.component.html',
	styleUrls: ['./it-password-reset.component.css'],
	imports: [
		CommonModule,
		FormsModule,
		ReactiveFormsModule,
		SelectDropdownComponent,
		MaterialModule,
		SharedModule
	],
	changeDetection: ChangeDetectionStrategy.OnPush
})
export class ItPasswordResetComponent implements OnInit, OnDestroy {
	private readonly userSubscription!: Subscription;
	userData: any;

	employees: any[] = [];
	selectedEmployee: any = null;

	isStrongPassword: boolean = false;
	itPasswordForm!: FormGroup;

	hideCurrentPassword = true;
	hideNewPassword = true;
	hideConfirmPassword = true;

	submitted = false;

	passwordErrors: any = null;

	constructor(
		private readonly fb: FormBuilder,
		private readonly toastr: ToastrService,
		private readonly authService: AuthService,
		private readonly sharedService: SharedService,
		public dialogRef: MatDialogRef<ItPasswordResetComponent>,
		private readonly reviewService: ReviewEntriesService
	) {
		this.userSubscription = this.authService.currentUser$.subscribe(
			(user) => {
				this.userData = user;
				this.isStrongPassword = user?.complexPasswordRequirement;
			}
		);
	}

	ngOnInit(): void {
		this.itPasswordForm = this.fb.group(
			{
				employee: [
					'',
					{
						validators: [Validators.required]
					}
				],

				newPassword: [
					'',
					[
						Validators.required,
						// Dynamic minimum length based on isStrongPassword
						Validators.minLength(this.isStrongPassword ? 8 : 4),
						this.disallowedPasswordCharsValidator,
						this.isStrongPassword
							? this.passwordStrengthValidator
							: null
					].filter((validator) => validator !== null)
				],
				confirmPassword: ['', Validators.required]
			},
			{ validators: this.passwordMatchValidator }
		);

		this.passwordErrors = {
			lowercase: 'at least one lowercase character',
			uppercase: 'at least one uppercase character',
			digit: 'at least one number',
			specialCharacter: 'at least one special character',
			disallowedChars: 'cannot contain ' + `(+ = [ ] \ ;')`,
			minLength: `at least ${this.isStrongPassword ? 8 : 4} characters`
		};

		this.getEmployees();
	}

	disallowedPasswordCharsValidator(
		control: AbstractControl
	): ValidationErrors | null {
		const password = control.value;
		if (!password) return null;
		return /[+=\[\]\\;'/]/.test(password) ? { disallowedChars: true } : null;
	}

	passwordStrengthValidator(control: any) {
		const password = control.value;
		if (!password) return null;

		const errors: any = {};

		if (!/(?=.*[a-z])/.test(password)) {
			errors.lowercase = true;
		}
		if (!/(?=.*[A-Z])/.test(password)) {
			errors.uppercase = true;
		}
		if (!/(?=.*\d)/.test(password)) {
			errors.digit = true;
		}
		if (!/(?=.*[!@#$%^&*(),.?":{}|<>_-])/.test(password)) {
			errors.specialCharacter = true;
		}
		if (password.trim().length < 8) {
			errors.minLength = true;
		}

		return Object.keys(errors).length > 0 ? errors : null;
	}

	getEmployees() {
		this.reviewService.getAllEmployees().subscribe((d) => {
			this.employees = d.map((employee: any) => ({
				value: employee.EmployeeNumber,
				label: `${employee.LastName}, ${employee.FirstName} (${employee.EmployeeNumber})`
			}));
			// this.selectedEmployee = this.employees[0];
		});
	}

	passwordMatchValidator(formGroup: FormGroup) {
		const newPasswordControl = formGroup.get('newPassword');
		const confirmPasswordControl = formGroup.get('confirmPassword');

		if (newPasswordControl && confirmPasswordControl) {
			const newPassword = newPasswordControl.value;
			const confirmPassword = confirmPasswordControl.value;

			if (newPassword !== confirmPassword) {
				confirmPasswordControl.setErrors({ passwordMismatch: true });
			} else {
				confirmPasswordControl.setErrors(null);
			}
		}
	}

	updatePassword() {
		this.submitted = true;
		if (this.itPasswordForm.valid) {
			let fieldData = this.itPasswordForm.value;
			// Perform password update logic here
			this.sharedService
				.updateEmployeePassword(
					fieldData?.employee,
					fieldData.newPassword,
					true
				)
				.subscribe({
					next: (response) => {
						this.toastr.clear();
						this.toastr.success(
							'Employee password UPDATED successfully.',
							'Success',
							{
								positionClass: 'toast-bottom-left',
								toastClass: 'toast-icon custom-toast-success'
							}
						);
						this.dialogRef.close();
					},
					error: (error) => {
						console.log('Error with password update: ', error);
						this.toastr.clear();
						this.toastr.error(
							'Error with password update!',
							'Error',
							{
								positionClass: 'toast-bottom-left',
								toastClass: 'toast-icon custom-toast-error'
							}
						);
					}
				});
		}
	}

	toggleVisibility(field: string): void {
		if (field === 'current') {
			this.hideCurrentPassword = !this.hideCurrentPassword;
		} else if (field === 'new') {
			this.hideNewPassword = !this.hideNewPassword;
		} else if (field === 'confirm') {
			this.hideConfirmPassword = !this.hideConfirmPassword;
		}
	}

	closePasswordDialog(): void {
		this.dialogRef.close();
	}

	@HostListener('copy', ['$event']) blockCopy(event: ClipboardEvent) {
		event.preventDefault();
	}

	ngOnDestroy(): void {
		if (this.userSubscription) {
			this.userSubscription.unsubscribe();
		}
	}
}
